import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Banner, Button, FieldText, LoadingAnimation, PageHeader, Tabs } from "@knowtex-ai/components";
import { Search, Trash, UploadSquare } from "iconoir-react";
import FoiaPageShell from "./FoiaPageShell";
import { fetchFoiaRequests, postFoiaAction } from "./api";
import { formatDate, formatRelativeTime, getStatusChip, isClosed } from "./foiaUtils";
import { useToast } from "../../hooks/use-toast";

const TASK_KINDS = [
  {
    kind: "retention",
    taskType: "RETENTION",
    label: "Retention",
    stepKey: "engineering_retain",
    actionCode: "USER_RETENTION_CONFIRMED",
    actionLabel: "Mark retained and sent",
    successLabel: "Retained and sent",
    icon: UploadSquare,
  },
  {
    kind: "deletion",
    taskType: "DELETION",
    label: "Deletion",
    stepKey: "engineering_delete",
    actionCode: "USER_DELETION_CONFIRMED",
    actionLabel: "Mark deleted",
    successLabel: "Deleted",
    icon: Trash,
  },
];

function tasksForRequest(request) {
  return TASK_KINDS.flatMap((taskKind) => {
    const task = (request.tasks || []).find((candidate) => candidate.type === taskKind.taskType);
    if (!task) return [];
    const isOpen = task.status !== "COMPLETED";
    const isFrozenByDeletion = taskKind.kind === "retention" && isOpen && request.deletionRequested;
    if (isFrozenByDeletion || (isOpen && isClosed(request))) return [];
    return [
      {
        key: `${request.id}:${taskKind.kind}`,
        request,
        taskKind,
        isOpen,
        statusChip: getStatusChip(request),
        completedAt: task.completedAt,
      },
    ];
  });
}

export default function FoiaEngineeringTasks({ setAuth, setUserOrg }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("open");

  const reload = () => fetchFoiaRequests().then((data) => setRequests(data?.requests || []));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch((err) => {
        if (!cancelled) {
          toast({ title: "Couldn't load Engineering tasks", description: err.message, variant: "destructive" });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { open, completed } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchesQuery = (r) => !q || r.trackingNumber.toLowerCase().includes(q);

    const tasks = requests.filter(matchesQuery).flatMap(tasksForRequest);
    const openTasks = tasks
      .filter((task) => task.isOpen)
      .sort((a, b) => new Date(a.request.dueDate) - new Date(b.request.dueDate));
    const completedTasks = tasks
      .filter((task) => !task.isOpen)
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

    return { open: openTasks, completed: completedTasks };
  }, [requests, query]);

  const tabs = [
    { id: "open", label: `Open (${open.length})`, onClick: () => setActiveTab("open") },
    { id: "completed", label: `Recently completed (${completed.length})`, onClick: () => setActiveTab("completed") },
  ];

  const rows = activeTab === "open" ? open : completed;

  const markDone = async ({ request, taskKind }) => {
    try {
      const result = await postFoiaAction(request.id, { actionCode: taskKind.actionCode });
      if (result?.applied === false) {
        toast({ title: `Already marked ${taskKind.successLabel.toLowerCase()}`, description: "Refreshing with the latest state." });
      }
      await reload();
    } catch (err) {
      toast({
        title: `Couldn't mark this request ${taskKind.successLabel.toLowerCase()}`,
        description: err.message,
        variant: "destructive",
      });
    }
  };

  return (
    <FoiaPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <PageHeader title="Engineering Tasks" subtitle="FOIA requests waiting on Engineering to retain a recording or delete data." />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <Tabs tabs={tabs} activeTab={activeTab} />
        <div className="max-w-xs w-full">
          <FieldText icon={Search} placeholder="Search by tracking number" value={query} onValueChange={setQuery} aria-label="Search by tracking number" />
        </div>
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-gray-200 bg-white">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-400">
            <LoadingAnimation size={24} />
            <span className="text-sm">Loading Engineering tasks…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6">
            <Banner variant="info">
              {activeTab === "open" ? "No open Engineering tasks right now." : "Nothing completed recently."}
            </Banner>
          </div>
        ) : (
          <table className="foia-match-table foia-match-table--edge">
            <thead>
              <tr>
                <th>Tracking #</th>
                <th>Task</th>
                <th>Due date</th>
                <th>Status</th>
                <th>{activeTab === "open" ? "Action" : "Completed"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((task) => {
                const { request, taskKind } = task;
                return (
                  <tr key={task.key}>
                    <td>
                      <button
                        type="button"
                        className="font-mono text-sm font-semibold text-ktx-purple hover:underline"
                        onClick={() => navigate(`/foia/requests/${request.id}`)}
                      >
                        {request.trackingNumber}
                      </button>
                    </td>
                    <td>{taskKind.label}</td>
                    <td>{taskKind.kind === "deletion" ? "—" : formatDate(request.dueDate)}</td>
                    <td>
                      <Badge variant={task.statusChip.variant} size="small">
                        {task.statusChip.label}
                      </Badge>
                    </td>
                    <td>
                      {activeTab === "open" ? (
                        <Button
                          size="small"
                          icon={taskKind.icon}
                          variant="primary-alt"
                          successLabel={taskKind.successLabel}
                          onClickAsync={() => markDone(task)}
                        >
                          {taskKind.actionLabel}
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-500">{formatRelativeTime(task.completedAt)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </FoiaPageShell>
  );
}
